import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { RefreshCw, Trash2 } from 'lucide-react';
import { adminApi } from '@/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { formatDateTime, formatRub } from '@/lib/utils';
import { PROVIDER, TX_STATUS, ruLabel } from '@/lib/labels';

export function AdminTransactions() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['admin', 'transactions'],
    queryFn: () => adminApi.transactions(),
  });

  const recheck = useMutation({
    mutationFn: (id: number) => adminApi.recheckTransaction(id),
    onSuccess: (r: any) => {
      if (r?.alreadyPaid) {
        toast.info('Транзакция уже была оплачена');
      } else if (r?.paid && r?.applied?.changed) {
        toast.success(`Зачислено: ${formatRub(r.applied.credited)}`);
      } else {
        toast.info(`Статус у провайдера: ${r?.providerStatus ?? 'неизвестен'}`);
      }
      qc.invalidateQueries({ queryKey: ['admin', 'transactions'] });
      qc.invalidateQueries({ queryKey: ['admin', 'stats'] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (err: AxiosError<{ message?: string }>) => {
      const detail = err.response?.data?.message;
      toast.error(detail ? `Ошибка: ${detail}` : 'Не удалось проверить');
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminApi.deleteTransaction(id),
    onSuccess: () => {
      toast.success('Транзакция удалена');
      qc.invalidateQueries({ queryKey: ['admin', 'transactions'] });
      qc.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
    onError: () => toast.error('Не удалось удалить'),
  });

  return (
    <div>
      <h1 className="text-3xl font-extrabold text-eol-white mb-6">Транзакции</h1>
      <Card>
        <CardContent className="pt-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-eol-desc text-left uppercase text-xs">
                  <th className="py-2 pr-4">ID</th>
                  <th className="py-2 pr-4">Пользователь</th>
                  <th className="py-2 pr-4">Провайдер</th>
                  <th className="py-2 pr-4">Внеш. ID</th>
                  <th className="py-2 pr-4">Сумма</th>
                  <th className="py-2 pr-4">Статус</th>
                  <th className="py-2 pr-4">Создано</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(data as any[])?.map((t) => (
                  <tr key={t.id} className="border-t border-white/5">
                    <td className="py-2 pr-4 text-eol-desc">{t.id}</td>
                    <td className="py-2 pr-4 text-eol-white">{t.username}</td>
                    <td className="py-2 pr-4">{ruLabel(PROVIDER, t.provider)}</td>
                    <td className="py-2 pr-4 text-xs text-eol-desc font-mono">{t.externalId}</td>
                    <td className="py-2 pr-4 text-eol-pink font-semibold">
                      {formatRub(t.amountMinor)}{' '}
                      {t.currency !== 'RUB' && (
                        <span className="text-xs text-eol-desc">{t.currency}</span>
                      )}
                    </td>
                    <td className="py-2 pr-4">
                      <Badge
                        variant={
                          t.status === 'paid'
                            ? 'success'
                            : t.status === 'failed'
                              ? 'danger'
                              : 'secondary'
                        }
                      >
                        {ruLabel(TX_STATUS, t.status)}
                      </Badge>
                    </td>
                    <td className="py-2 pr-4 text-eol-desc">{formatDateTime(t.createdAt)}</td>
                    <td className="py-2 pr-2">
                      <div className="flex items-center gap-2 justify-end">
                        {t.status === 'pending' && (t.provider === 'yookassa' || t.provider === 'cryptobot') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => recheck.mutate(t.id)}
                            disabled={recheck.isPending}
                            className="gap-1"
                            title="Спросить у провайдера актуальный статус"
                          >
                            <RefreshCw className="h-3 w-3" /> Перепроверить
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="outline"
                          onClick={() => {
                            if (confirm(`Удалить транзакцию #${t.id}? Баланс пользователя НЕ откатится.`)) {
                              remove.mutate(t.id);
                            }
                          }}
                          disabled={remove.isPending}
                          title="Удалить запись (баланс не откатывается)"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
